import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AnalyticsRoundedIcon from '@mui/icons-material/AnalyticsRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Popover,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import { fetchPatternPerformanceBreakdown } from '../../api/adminPatternPerformance';
import {
  BUCKET_TIMEFRAMES,
  DEFAULT_BUCKET_ENGINE_VERSIONS,
  DEFAULT_BUCKET_TIMEFRAMES,
  HISTORICAL_PATTERN_ENGINE_VERSIONS,
  NO_CLEAR_PATTERN_KEY,
  resolvePatternEngineVersionLabel,
} from '../pattern-scanner/scannerExperienceShared';
import { managedPatternOptions } from '../pattern-scanner/patternTutorialData';

const PAGE_COLORS = {
  ink: '#17323c',
  muted: '#4f646f',
  success: '#2f7d5d',
  warning: '#b57422',
  danger: '#9c4f4f',
};

const SCORE_BUCKETS = ['<50', '50-59', '60-69', '70-79', '80-89', '90-100'];
const PATTERN_FILTER_OPTIONS = managedPatternOptions
  .filter(({ value }) => value !== NO_CLEAR_PATTERN_KEY)
  .sort((left, right) => left.value.localeCompare(right.value));

const POP_DETECTIONS = 'détections de la tranche';

const TP_HIGHLIGHT_THRESHOLDS = { tp1: 60, tp2: 40, tp3: 20 };

/**
 * Metric catalogue. Historical engines keep the score-tranche detections denominator. V5.2 TP metrics
 * use the definitive-observation denominator below; BO/Inv. keep detections. TP metrics carry a
 * `thresholdKey` used for the fixed visual highlight.
 */
const METRIC_DEFS = [
  {
    key: 'breakout',
    short: 'BO',
    full: 'Breakout',
    population: POP_DETECTIONS,
    definition: 'Part des détections de la tranche ayant cassé le niveau de breakout.',
    color: PAGE_COLORS.ink,
    sortable: true,
  },
  {
    key: 'invalidation',
    short: 'Inv.',
    full: 'Invalidation du pattern',
    population: POP_DETECTIONS,
    definition: "Part des détections de la tranche invalidées. Une invalidation peut survenir après l'atteinte d'un ou plusieurs objectifs TP.",
    color: PAGE_COLORS.danger,
    sortable: true,
  },
  {
    key: 'invalidationBeforeTp1',
    short: 'Échec TP1',
    full: 'Invalidation avant TP1',
    population: POP_DETECTIONS,
    definition: "Part des détections de la tranche dont l'évaluation TP définitive a été invalidée avant l'atteinte du premier objectif (échec avant TP1).",
    color: PAGE_COLORS.danger,
    sortable: true,
  },
  {
    key: 'tp1',
    short: 'TP1',
    full: 'TP1 atteint',
    population: POP_DETECTIONS,
    definition: 'Part des détections de la tranche ayant atteint le premier objectif (évaluations TP définitives).',
    color: PAGE_COLORS.success,
    thresholdKey: 'tp1',
    sortable: true,
  },
  {
    key: 'tp2',
    short: 'TP2',
    full: 'TP2 atteint',
    population: POP_DETECTIONS,
    definition: 'Part des détections de la tranche ayant atteint le deuxième objectif (évaluations TP définitives).',
    color: PAGE_COLORS.success,
    thresholdKey: 'tp2',
    sortable: true,
  },
  {
    key: 'tp3',
    short: 'TP3',
    full: 'TP3 atteint',
    population: POP_DETECTIONS,
    definition: 'Part des détections de la tranche ayant atteint le troisième objectif (évaluations TP définitives).',
    color: PAGE_COLORS.success,
    thresholdKey: 'tp3',
    sortable: true,
  },
];

const COLUMN_WIDTHS = {
  pattern: '32%',
  breakout: '8%',
  invalidation: '8%',
  invalidationBeforeTp1: '12%',
  tp1: '8%',
  tp2: '8%',
  tp3: '8%',
  details: '8%',
};

const SORTABLE_COLUMNS = [
  { key: 'pattern', label: 'Pattern / Version' },
  ...METRIC_DEFS.map((def) => ({ key: def.key, label: `${def.short} — ${def.full}` })),
];

function toNumber(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function formatPercent(value, fallback = '—') {
  const numeric = toNumber(value);
  if (numeric === null) {
    return fallback;
  }
  return `${(numeric * 100).toFixed(1)}%`;
}

function toCount(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
}

function compareEngineVersions(left, right) {
  if (left === right) {
    return 0;
  }
  const leftNumber = Number(left);
  const rightNumber = Number(right);
  if (Number.isFinite(leftNumber) && Number.isFinite(rightNumber)) {
    return leftNumber - rightNumber;
  }
  return String(left).localeCompare(String(right));
}

/**
 * Displays the CANONICAL engine version actually carried by the row. The API returns already-canonical
 * strings ("v4.3", "v5.1"): passing them to the numeric engine-id helper produced "V?" (regression).
 * Accepts both a canonical string and a raw technical id.
 */
function formatPatternVersion(value) {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  const numeric = Number(value);
  if (Number.isFinite(numeric)) {
    return resolvePatternEngineVersionLabel(numeric).toUpperCase();
  }
  return String(value).trim().toUpperCase();
}

function versionRank(value) {
  const match = /v?(\d+)(?:\.(\d+))?/i.exec(String(value || ''));
  if (!match) {
    return [999, 999];
  }
  return [Number(match[1]), match[2] ? Number(match[2]) : 0];
}

function compareCanonicalVersion(left, right) {
  const [leftMajor, leftMinor] = versionRank(left);
  const [rightMajor, rightMinor] = versionRank(right);
  return (leftMajor - rightMajor) || (leftMinor - rightMinor);
}

function LowSampleChip({ lowSample, threshold }) {
  if (!lowSample) {
    return null;
  }
  return (
    <Chip
      size="small"
      label={`Echantillon < ${threshold}`}
      sx={{
        height: 20,
        bgcolor: alpha(PAGE_COLORS.warning, 0.12),
        color: PAGE_COLORS.warning,
        fontWeight: 700,
      }}
    />
  );
}

function ProvisionalMarker() {
  return (
    <Tooltip title="Échantillon partiel : certains taux (TP1/TP2/TP3, échec TP1) sont provisoires tant que le backfill historique n'est pas complet.">
      <Box
        component="span"
        tabIndex={0}
        aria-label="Taux provisoires (échantillon partiel)"
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: PAGE_COLORS.warning,
          display: 'inline-block',
          flex: '0 0 auto',
        }}
      />
    </Tooltip>
  );
}

function isV52Row(row) {
  const version = String(row?.engineVersion || '').trim().toLowerCase();
  return version === 'v5.2' || version === '5.2' || version === 'v9' || version === '9';
}

function v52Stats(row) {
  return isV52Row(row) && row.v52TakeProfits ? row.v52TakeProfits : null;
}

function isProvisional(row) {
  const v52 = v52Stats(row);
  if (v52) {
    return v52.coverageComplete !== true && toCount(v52.definitiveCount) > 0;
  }
  const takeProfits = row?.takeProfits;
  return Boolean(takeProfits)
    && takeProfits.coverageComplete !== true
    && toCount(takeProfits.definitiveCount) > 0;
}

function formatCount(value) {
  const numeric = toNumber(value);
  return numeric === null ? '—' : String(numeric);
}

function detectionDenominator(row) {
  return toNumber(row?.detections);
}

function legacyMetricNumbers(row, key) {
  const tp = row?.takeProfits;
  const denominator = detectionDenominator(row);
  let numerator = null;
  switch (key) {
    case 'breakout': numerator = toNumber(row?.breakoutCount); break;
    case 'invalidation': numerator = toNumber(row?.invalidationCount); break;
    case 'invalidationBeforeTp1': numerator = toNumber(tp?.invalidationBeforeTp1Count); break;
    case 'tp1': numerator = toNumber(tp?.tp1Count); break;
    case 'tp2': numerator = toNumber(tp?.tp2Count); break;
    case 'tp3': numerator = toNumber(tp?.tp3Count); break;
    default: numerator = null;
  }
  return {
    numerator,
    denominator,
    rate: numerator !== null && denominator !== null && denominator > 0
      ? numerator / denominator
      : null,
    population: 'détections de la tranche',
    denominatorLabel: 'Détections',
    explicitFraction: false,
  };
}

function v52TpLevel(v52, key) {
  if (key === 'tp1') return v52.tp1;
  if (key === 'tp2') return v52.tp2;
  if (key === 'tp3') return v52.tp3;
  return null;
}

function v52MetricNumbers(row, key) {
  const v52 = v52Stats(row);
  if (!v52) {
    if (isV52Row(row) && ['invalidationBeforeTp1', 'tp1', 'tp2', 'tp3'].includes(key)) {
      return {
        numerator: null,
        denominator: null,
        rate: null,
        population: 'statistiques V5.2 indisponibles (groupement par version requis)',
        denominatorLabel: 'Observations définitives',
        explicitFraction: true,
      };
    }
    return null;
  }
  if (key === 'breakout' || key === 'invalidation') {
    return legacyMetricNumbers(row, key);
  }
  if (key === 'invalidationBeforeTp1') {
    const numerator = toNumber(v52.invalidationBeforeTp1Count);
    const denominator = toNumber(v52.definitiveCount);
    return {
      numerator,
      denominator,
      rate: numerator !== null && denominator !== null && denominator > 0
        ? numerator / denominator
        : null,
      population: 'observations définitives',
      denominatorLabel: 'Observations définitives',
      definition: 'Part des observations définitives invalidées avant l\'atteinte de TP1.',
      explicitFraction: true,
    };
  }
  const level = v52TpLevel(v52, key);
  if (!level) {
    return legacyMetricNumbers(row, key);
  }
  const numerator = toNumber(level.definitiveCount);
  const denominator = toNumber(v52.definitiveCount);
  const backendRate = toNumber(level.definitiveRate);
  return {
    numerator,
    denominator,
    rate: backendRate !== null
      ? backendRate
      : (numerator !== null && denominator !== null && denominator > 0 ? numerator / denominator : null),
    population: 'observations définitives (taux conditionnel)',
    denominatorLabel: 'Observations définitives',
    definition: 'Taux conditionnel parmi les observations définitives. Le taux sur la cohorte éligible complète est affiché dans le détail.',
    explicitFraction: true,
  };
}

function metricNumbers(row, key) {
  return v52MetricNumbers(row, key) || legacyMetricNumbers(row, key);
}

function metricRate(row, key) {
  return metricNumbers(row, key).rate;
}

function buildMetrics(row) {
  return METRIC_DEFS.map((def) => {
    const numbers = metricNumbers(row, def.key);
    const rate = numbers.rate;
    const available = rate !== null;
    const threshold = def.thresholdKey ? TP_HIGHLIGHT_THRESHOLDS[def.thresholdKey] : null;
    const highlighted = Boolean(def.thresholdKey)
      && available
      && (rate * 100) >= threshold;
    const countText = numbers.explicitFraction
      ? `${formatCount(numbers.numerator)}/${formatCount(numbers.denominator)}`
      : (numbers.denominator === 0 ? '0' : formatCount(numbers.numerator));
    return {
      ...def,
      definition: numbers.definition || def.definition,
      population: numbers.population,
      denominatorLabel: numbers.denominatorLabel,
      rate,
      numerator: numbers.numerator,
      denominator: numbers.denominator,
      available,
      highlighted,
      text: available ? formatPercent(rate) : '—',
      countText,
    };
  });
}

function getSortValue(row, key) {
  switch (key) {
    case 'pattern': return String(row.pattern || '');
    default: return metricRate(row, key);
  }
}

function compareRows(left, right, key, direction) {
  const factor = direction === 'desc' ? -1 : 1;
  const leftValue = getSortValue(left, key);
  const rightValue = getSortValue(right, key);
  const leftEmpty = leftValue === null || leftValue === undefined;
  const rightEmpty = rightValue === null || rightValue === undefined;
  if (leftEmpty && rightEmpty) {
    return tieBreak(left, right);
  }
  if (leftEmpty) {
    return 1; // nulls always last, both directions
  }
  if (rightEmpty) {
    return -1;
  }
  let comparison;
  if (typeof leftValue === 'number' && typeof rightValue === 'number') {
    comparison = leftValue - rightValue;
  } else {
    comparison = String(leftValue).localeCompare(String(rightValue));
  }
  if (comparison !== 0) {
    return comparison * factor;
  }
  return tieBreak(left, right);
}

function tieBreak(left, right) {
  const patternComparison = String(left.pattern || '').localeCompare(String(right.pattern || ''));
  if (patternComparison !== 0) {
    return patternComparison;
  }
  return compareCanonicalVersion(left.engineVersion, right.engineVersion);
}

function MetricTooltip({ metric }) {
  return (
    <Box sx={{ maxWidth: 300 }}>
      <Typography variant="body2" sx={{ fontWeight: 800 }}>{metric.full}</Typography>
      <Typography variant="caption" sx={{ display: 'block' }}>{`Population : ${metric.population}.`}</Typography>
      {metric.definition ? (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>{metric.definition}</Typography>
      ) : null}
      <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontWeight: 700 }}>
        {`Effectif : ${metric.countText} · ${metric.denominatorLabel || 'Détections'} : ${formatCount(metric.denominator)}`}
      </Typography>
    </Box>
  );
}

function MetricValue({ metric }) {
  return (
    <Tooltip title={<MetricTooltip metric={metric} />} enterTouchDelay={0} leaveTouchDelay={6000}>
      <Box
        component="span"
        tabIndex={0}
        aria-label={`${metric.full} : ${metric.available ? metric.text : 'non disponible'}, nombre ${metric.countText}`}
        sx={{
          display: 'inline-flex',
          flexDirection: 'column',
          alignItems: 'center',
          cursor: 'help',
          outline: 'none',
          '&:focus-visible': { outline: `2px solid ${alpha(PAGE_COLORS.ink, 0.5)}`, borderRadius: 0.5 },
        }}
      >
        <Box
          component="span"
          data-highlighted={metric.highlighted ? 'true' : 'false'}
          sx={{
            fontWeight: metric.highlighted ? 700 : 400,
            fontSize: 14,
            lineHeight: 1.1,
            whiteSpace: 'nowrap',
            color: metric.available ? metric.color : PAGE_COLORS.muted,
          }}
        >
          {metric.available ? metric.text : '—'}
        </Box>
        <Box component="span" sx={{ fontSize: 11, lineHeight: 1.2, color: PAGE_COLORS.muted }}>
          {metric.countText}
        </Box>
      </Box>
    </Tooltip>
  );
}

function v52ObservedNonDefinitive(v52) {
  return [v52?.tp1, v52?.tp2, v52?.tp3].reduce((total, level) => {
    if (!level) return total;
    const observed = toCount(level.observedCount);
    const definitive = toCount(level.definitiveCount);
    return total + Math.max(0, observed - definitive);
  }, 0);
}

function isV52LowSample(row, threshold) {
  const v52 = v52Stats(row);
  if (!v52) return false;
  const definitive = toCount(v52.definitiveCount);
  return definitive > 0 && definitive < threshold;
}

function V52Summary({ row, threshold }) {
  const v52 = v52Stats(row);
  if (!v52) return null;
  const definitive = toCount(v52.definitiveCount);
  const eligible = toCount(v52.eligibleCount);
  return (
    <Stack spacing={0.15} sx={{ mt: 0.35 }}>
      <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, lineHeight: 1.15 }}>
        {`Définitifs : ${definitive}/${eligible} · Couverture : ${formatPercent(v52.coverageRate)}`}
      </Typography>
      <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, lineHeight: 1.15 }}>
        {`Non évalués : ${formatCount(v52.notEvaluatedCount)} · UNKNOWN : ${formatCount(v52.unknownCount)} · AMBIGUOUS : ${formatCount(v52.ambiguousCount)}`}
      </Typography>
      <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, lineHeight: 1.15 }}>
        {`TP observés non définitifs : ${v52ObservedNonDefinitive(v52)} · Expirés sans TP : ${formatCount(v52.windowExpiredNoTpCount)} · SL : ${formatCount(v52.slCount)}`}
      </Typography>
      {definitive === 0 ? (
        <Typography variant="caption" sx={{ color: PAGE_COLORS.warning, lineHeight: 1.15, fontWeight: 700 }}>
          Aucune observation définitive
        </Typography>
      ) : null}
      {isV52LowSample(row, threshold) ? (
        <LowSampleChip lowSample threshold={threshold} />
      ) : null}
    </Stack>
  );
}

function PatternCell({ row, showVersion, provisional, threshold }) {
  return (
    <Box>
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
        <Typography
          variant="body2"
          title={row.pattern}
          sx={{ fontWeight: 700, color: PAGE_COLORS.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {row.pattern}
        </Typography>
        {showVersion ? (
          <Chip
            size="small"
            label={formatPatternVersion(row.engineVersion)}
            sx={{ height: 20, bgcolor: alpha(PAGE_COLORS.ink, 0.06), color: PAGE_COLORS.muted, fontWeight: 800 }}
          />
        ) : null}
        {provisional ? <ProvisionalMarker /> : null}
      </Stack>
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.25 }}>
        <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, lineHeight: 1.1 }}>
          {`Détections : ${formatCount(row.detections)}`}
        </Typography>
        {!v52Stats(row) ? <LowSampleChip lowSample={row.lowSample} threshold={threshold} /> : null}
      </Stack>
      <V52Summary row={row} threshold={threshold} />
      {isV52Row(row) && !v52Stats(row) ? (
        <Typography variant="caption" sx={{ display: 'block', color: PAGE_COLORS.warning, lineHeight: 1.15, fontWeight: 700 }}>
          Statistiques V5.2 indisponibles (groupement par version requis)
        </Typography>
      ) : null}
    </Box>
  );
}

function V52Details({ row, threshold }) {
  const v52 = v52Stats(row);
  if (!v52) return null;
  const tpRows = [
    ['TP1', v52.tp1],
    ['TP2', v52.tp2],
    ['TP3', v52.tp3],
  ];
  return (
    <Box sx={{ mt: 1.25, pt: 1.25, borderTop: `1px solid ${alpha(PAGE_COLORS.ink, 0.12)}` }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: PAGE_COLORS.ink }}>
        V5.2 · Statistiques TP/SL explicites
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', color: PAGE_COLORS.muted }}>
        {`Détections brutes / dédupliquées : ${formatCount(v52.rawDetectionCount)} / ${formatCount(v52.deduplicatedDetectionCount)}`}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', color: PAGE_COLORS.muted }}>
        {`Éligibles : ${formatCount(v52.eligibleCount)} · Non évaluées : ${formatCount(v52.notEvaluatedCount)} · Calculées : ${formatCount(v52.calculatedCount)}`}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', color: PAGE_COLORS.muted }}>
        {`calc1 / calc2 : ${formatCount(v52.calc1Count)} / ${formatCount(v52.calc2Count)} · Fenêtres ouvertes / fermées : ${formatCount(v52.openWindowCount)} / ${formatCount(v52.closedWindowCount)}`}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', color: PAGE_COLORS.muted }}>
        {`Définitifs : ${formatCount(v52.definitiveCount)} · PENDING : ${formatCount(v52.pendingCount)} · UNKNOWN : ${formatCount(v52.unknownCount)} · AMBIGUOUS : ${formatCount(v52.ambiguousCount)}`}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', color: PAGE_COLORS.muted }}>
        {`Expirés sans TP : ${formatCount(v52.windowExpiredNoTpCount)} · SL dans la fenêtre : ${formatCount(v52.slCount)} · Invalidations avant TP1 : ${formatCount(v52.invalidationBeforeTp1Count)}`}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', color: PAGE_COLORS.muted }}>
        {`Couverture : ${formatPercent(v52.coverageRate)} · Échantillon insuffisant : ${isV52LowSample(row, threshold) || toCount(v52.definitiveCount) === 0 ? 'oui' : 'non'}`}
      </Typography>
      <Divider sx={{ my: 1 }} />
      <Stack spacing={0.8}>
        {tpRows.map(([label, level]) => {
          if (!level) return null;
          const observed = toCount(level.observedCount);
          const definitive = toCount(level.definitiveCount);
          return (
            <Box key={label}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{label}</Typography>
              <Typography variant="caption" sx={{ display: 'block', color: PAGE_COLORS.muted }}>
                {`Observés : ${observed} · définitifs : ${definitive} · non définitifs : ${Math.max(0, observed - definitive)}`}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', color: PAGE_COLORS.muted }}>
                {`Taux observé : ${formatPercent(level.observedRate)} (${observed}/${formatCount(v52.calculatedCount)}) · taux définitif : ${formatPercent(level.definitiveRate)} (${definitive}/${formatCount(v52.definitiveCount)}) · taux cohorte éligible : ${formatPercent(level.eligibleRate)} (${definitive}/${formatCount(v52.eligibleCount)})`}
              </Typography>
            </Box>
          );
        })}
      </Stack>
      <Typography variant="caption" sx={{ display: 'block', mt: 1, color: PAGE_COLORS.muted }}>
        {`Dernier calcul : ${v52.latestComputedAt || '—'} · Dernière bougie traitée : ${v52.latestLastProcessedCandleAt || '—'}`}
      </Typography>
    </Box>
  );
}

function RowDetailsContent({ row, threshold }) {
  const metrics = buildMetrics(row);
  const v52 = v52Stats(row);
  return (
    <Box sx={{ p: 2, maxWidth: 460 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: PAGE_COLORS.ink }}>
          {row.pattern || 'Pattern'}
        </Typography>
        <Chip size="small" label={formatPatternVersion(row.engineVersion)} sx={{ height: 20 }} />
      </Stack>
      <Typography variant="caption" sx={{ color: PAGE_COLORS.muted }}>
        {`Détections : ${formatCount(row.detections)}`}
      </Typography>
      <Divider sx={{ my: 1 }} />
      <Stack spacing={0.75}>
        {metrics.map((metric) => (
          <Stack key={metric.key} direction="row" justifyContent="space-between" spacing={2} alignItems="baseline">
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{metric.full}</Typography>
              <Typography variant="caption" sx={{ color: PAGE_COLORS.muted }}>{metric.population}</Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: metric.highlighted ? 700 : 400, color: metric.available ? metric.color : PAGE_COLORS.muted }}
              >
                {metric.available ? metric.text : '—'}
              </Typography>
              <Typography variant="caption" sx={{ color: PAGE_COLORS.muted }}>{metric.countText}</Typography>
            </Box>
          </Stack>
        ))}
      </Stack>
      {v52 ? <V52Details row={row} threshold={threshold} /> : null}
      <Typography variant="caption" sx={{ display: 'block', mt: 1.25, color: PAGE_COLORS.muted }}>
        {v52
          ? 'V5.2 : TP1/TP2/TP3 et échec TP1 utilisent les observations définitives ; BO/Inv. utilisent les détections. Les UNKNOWN/PENDING ne sont jamais comptés comme échecs.'
          : isV52Row(row)
            ? 'Statistiques V5.2 indisponibles pour cette ligne : sélectionnez V5.2 avec groupement par version pour obtenir les dénominateurs définitifs.'
            : 'Moteurs historiques 6/7/8 : le pourcentage conserve le dénominateur historique du nombre de détections de la tranche.'}
      </Typography>
    </Box>
  );
}

function SortControl({ sort, onSort }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Select
        size="small"
        value={sort.key || 'pattern'}
        onChange={(event) => onSort(event.target.value)}
        inputProps={{ 'aria-label': 'Trier par' }}
        sx={{ minWidth: 180, bgcolor: '#fff' }}
      >
        {SORTABLE_COLUMNS.map((column) => (
          <MenuItem key={column.key} value={column.key}>{column.label}</MenuItem>
        ))}
      </Select>
      <Button
        size="small"
        variant="outlined"
        aria-label={sort.direction === 'asc' ? 'Tri croissant' : 'Tri décroissant'}
        onClick={() => onSort(sort.key, sort.direction === 'asc' ? 'desc' : 'asc')}
        sx={{ minWidth: 72 }}
      >
        {sort.direction === 'asc' ? '↑ Croissant' : '↓ Décroissant'}
      </Button>
    </Stack>
  );
}

function ConfigurablePatternPerformanceTable({ appliedFilters = {}, lowSampleThreshold = 30 }) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [selectedPattern, setSelectedPattern] = useState('');
  const [selectedVersions, setSelectedVersions] = useState(DEFAULT_BUCKET_ENGINE_VERSIONS);
  const [selectedTimeframes, setSelectedTimeframes] = useState(DEFAULT_BUCKET_TIMEFRAMES);
  const [selectedScoreBuckets, setSelectedScoreBuckets] = useState([]);
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [details, setDetails] = useState({ anchorEl: null, row: null });
  const [sort, setSort] = useState({ key: null, direction: 'asc' });

  const orderedVersions = [...HISTORICAL_PATTERN_ENGINE_VERSIONS].sort(compareEngineVersions);

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const requestFilters = { ...appliedFilters };
      // Les sélections locales sont l'autorité pour ce breakdown : les filtres
      // globaux correspondants ne doivent pas les neutraliser.
      delete requestFilters.version;
      delete requestFilters.timeframe;
      delete requestFilters.pattern;
      const request = {
        ...requestFilters,
        // Pattern is the mandatory grouping dimension for this screen.
        groupByPattern: true,
        engineVersions: selectedVersions.join(','),
        timeframes: selectedTimeframes.join(','),
        scoreBuckets: selectedScoreBuckets.join(','),
      };
      if (selectedPattern) {
        request.pattern = selectedPattern;
      }
      const payload = await fetchPatternPerformanceBreakdown(request);
      setRows(payload?.rows || []);
    } catch (loadError) {
      setRows(null);
      setError(loadError.message || 'Impossible de charger la segmentation par bucket de score.');
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, selectedPattern, selectedVersions, selectedTimeframes, selectedScoreBuckets]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const sortedRows = useMemo(() => {
    if (!rows?.length || !sort.key) {
      return rows || [];
    }
    // Sort the WHOLE loaded cohort (not only visible rows) with a stable tie-break.
    return [...rows].sort((left, right) => compareRows(left, right, sort.key, sort.direction));
  }, [rows, sort]);

  const toggleVersion = (version) => (event) => {
    const checked = event.target.checked;
    setSelectedVersions((current) => (
      checked ? [...current, version] : current.filter((item) => item !== version)
    ));
  };

  const toggleTimeframe = (timeframe) => (event) => {
    const checked = event.target.checked;
    setSelectedTimeframes((current) => (
      checked ? [...current, timeframe] : current.filter((item) => item !== timeframe)
    ));
  };

  const toggleScoreBucket = (bucket) => (event) => {
    const checked = event.target.checked;
    setSelectedScoreBuckets((current) => (
      checked ? [...current, bucket] : current.filter((item) => item !== bucket)
    ));
  };

  const handleSort = (key, forcedDirection) => {
    setSort((current) => {
      if (forcedDirection) {
        return { key, direction: forcedDirection };
      }
      if (current.key !== key) {
        return { key, direction: key === 'pattern' ? 'asc' : 'desc' };
      }
      return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
    });
  };

  const showVersionColumn = selectedVersions.length > 0;
  const openDetails = (event, row) => setDetails({ anchorEl: event.currentTarget, row });
  const closeDetails = () => setDetails({ anchorEl: null, row: null });

  const sortLabel = (key, label, align) => (
    <TableSortLabel
      active={sort.key === key}
      direction={sort.key === key ? sort.direction : 'asc'}
      onClick={() => handleSort(key)}
      aria-label={`Trier par ${label}`}
      sx={{ '& .MuiTableSortLabel-icon': { opacity: sort.key === key ? 1 : 0.3 } }}
    >
      {label}
    </TableSortLabel>
  );

  const renderDesktopTable = (list) => (
    <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 800, width: COLUMN_WIDTHS.pattern }}>
            {sortLabel('pattern', showVersionColumn ? 'Pattern / Version' : 'Pattern')}
          </TableCell>
          {METRIC_DEFS.map((def) => (
            <TableCell key={def.key} align="center" sx={{ fontWeight: 800, width: COLUMN_WIDTHS[def.key] }}>
              <Tooltip title={<MetricTooltip metric={def} />}>
                {sortLabel(def.key, def.short)}
              </Tooltip>
            </TableCell>
          ))}
          <TableCell align="center" sx={{ width: COLUMN_WIDTHS.details }} />
        </TableRow>
      </TableHead>
      <TableBody>
        {list.map((row, index) => {
          const metrics = buildMetrics(row);
          const provisional = isProvisional(row);
          return (
            <TableRow
              key={`${row.pattern || ''}-${row.timeframe || ''}-${row.engineVersion || ''}-${index}`}
              hover
            >
              <TableCell sx={{ py: 0.75 }}>
                <PatternCell row={row} showVersion={showVersionColumn} provisional={provisional} threshold={lowSampleThreshold} />
              </TableCell>
              {metrics.map((metric) => (
                <TableCell key={metric.key} align="center" sx={{ px: 0.5, py: 0.75 }}>
                  <MetricValue metric={metric} />
                </TableCell>
              ))}
              <TableCell align="center" sx={{ px: 0.25, py: 0.5 }}>
                <Tooltip title={<span>Détails : taux, nombres et dénominateurs</span>}>
                  <IconButton
                    size="small"
                    aria-label={`Détails statistiques ${row.pattern || ''} ${row.engineVersion || ''}`.trim()}
                    onClick={(event) => openDetails(event, row)}
                  >
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );

  const renderMobileCards = (list) => (
    <Stack spacing={1.25}>
      {list.map((row, index) => {
        const metrics = buildMetrics(row);
        const provisional = isProvisional(row);
        return (
          <Card
            key={`${row.pattern || ''}-${row.timeframe || ''}-${row.engineVersion || ''}-${index}`}
            variant="outlined"
            sx={{ borderRadius: 3 }}
          >
            <Box sx={{ p: 1.25 }}>
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                <Box sx={{ minWidth: 0 }}>
                  <PatternCell row={row} showVersion={showVersionColumn} provisional={provisional} threshold={lowSampleThreshold} />
                </Box>
                <IconButton
                  size="small"
                  aria-label={`Détails statistiques ${row.pattern || ''} ${row.engineVersion || ''}`.trim()}
                  onClick={(event) => openDetails(event, row)}
                >
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Stack>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                  gap: 1,
                  mt: 1.25,
                }}
              >
                {metrics.map((metric) => (
                  <Box key={metric.key} sx={{ minWidth: 0 }}>
                    <Tooltip title={<MetricTooltip metric={metric} />} enterTouchDelay={0} leaveTouchDelay={6000}>
                      <Box component="span" tabIndex={0} data-highlighted={metric.highlighted ? 'true' : 'false'} sx={{ cursor: 'help', outline: 'none' }}>
                        <Typography variant="caption" sx={{ display: 'block', color: PAGE_COLORS.muted, fontWeight: 800 }}>
                          {metric.short}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: metric.highlighted ? 700 : 400, color: metric.available ? metric.color : PAGE_COLORS.muted }}
                        >
                          {metric.available ? metric.text : '—'}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', color: PAGE_COLORS.muted }}>
                          {metric.countText}
                        </Typography>
                      </Box>
                    </Tooltip>
                  </Box>
                ))}
              </Box>
            </Box>
          </Card>
        );
      })}
    </Stack>
  );

  const renderBody = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} sx={{ color: PAGE_COLORS.ink }} />
        </Box>
      );
    }
    if (error) {
      return <Alert severity="error">{error}</Alert>;
    }
    if (!rows?.length) {
      return <Alert severity="info">Aucune donnée pour cette sélection.</Alert>;
    }

    return (
      <Box sx={{ width: '100%' }}>
        {isDesktop
          ? <TableContainer sx={{ width: '100%' }}>{renderDesktopTable(sortedRows)}</TableContainer>
          : renderMobileCards(sortedRows)}
        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: PAGE_COLORS.muted }}>
          V5.2 : TP1/TP2/TP3 et échec TP1 utilisent les observations définitives ; BO/Inv. utilisent
          les détections. Les moteurs historiques 6/7/8 conservent le dénominateur historique. Les
          statuts PENDING/UNKNOWN ne sont jamais des échecs implicites ; les taux restent provisoires
          tant que la couverture n'est pas complète.
        </Typography>
      </Box>
    );
  };

  return (
    <Paper sx={{ p: 3, borderRadius: 5, background: '#fff', border: `1px solid ${alpha(PAGE_COLORS.ink, 0.08)}` }}>
      <Stack spacing={2.1}>
        <Stack direction="row" spacing={1.1} alignItems="center">
          <AnalyticsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: PAGE_COLORS.ink }}>
              Analyse de performance par Score
            </Typography>
            <Typography variant="body2" sx={{ color: PAGE_COLORS.muted, mt: 0.35 }}>
              Segmentation par pattern et version moteur, croisée avec le bucket de score, filtrable par pattern, moteur, timeframe et score.
            </Typography>
          </Box>
        </Stack>

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          useFlexGap
          flexWrap="wrap"
          sx={{
            p: 1.5,
            borderRadius: 3,
            bgcolor: alpha(PAGE_COLORS.ink, 0.03),
            alignItems: { xs: 'flex-start', md: 'center' },
          }}
        >
          <Stack spacing={0.25} sx={{ minWidth: { xs: '100%', sm: 240 } }}>
            <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, fontWeight: 800, letterSpacing: 0.6 }}>
              Pattern
            </Typography>
            <FormControl size="small" fullWidth>
              <InputLabel id="pattern-performance-pattern-filter-label">Pattern</InputLabel>
              <Select
                labelId="pattern-performance-pattern-filter-label"
                label="Pattern"
                displayEmpty
                value={selectedPattern}
                onChange={(event) => setSelectedPattern(event.target.value)}
                renderValue={(value) => value || 'Tous les patterns'}
                inputProps={{ 'aria-label': 'Pattern' }}
                sx={{ bgcolor: '#fff' }}
              >
                <MenuItem value="">Tous les patterns</MenuItem>
                {PATTERN_FILTER_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Stack spacing={0.25}>
            <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, fontWeight: 800, letterSpacing: 0.6 }}>
              Versions moteur
            </Typography>
            <FormGroup row>
              {orderedVersions.map((version) => (
                <FormControlLabel
                  key={version}
                  control={(
                    <Checkbox
                      size="small"
                      checked={selectedVersions.includes(version)}
                      onChange={toggleVersion(version)}
                    />
                  )}
                  label={formatPatternVersion(version)}
                />
              ))}
            </FormGroup>
          </Stack>

          <Stack spacing={0.25}>
            <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, fontWeight: 800, letterSpacing: 0.6 }}>
              Timeframes
            </Typography>
            <FormGroup row>
              {BUCKET_TIMEFRAMES.map((option) => (
                <FormControlLabel
                  key={option.value}
                  control={(
                    <Checkbox
                      size="small"
                      checked={selectedTimeframes.includes(option.value)}
                      onChange={toggleTimeframe(option.value)}
                    />
                  )}
                  label={option.label}
                />
              ))}
            </FormGroup>
          </Stack>

          <Stack spacing={0.25}>
            <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, fontWeight: 800, letterSpacing: 0.6 }}>
              Scores
            </Typography>
            <FormGroup row>
              {SCORE_BUCKETS.map((bucket) => (
                <FormControlLabel
                  key={bucket}
                  control={(
                    <Checkbox
                      size="small"
                      checked={selectedScoreBuckets.includes(bucket)}
                      onChange={toggleScoreBucket(bucket)}
                    />
                  )}
                  label={bucket}
                />
              ))}
            </FormGroup>
          </Stack>

          <Stack spacing={0.25} sx={{ flex: 1, alignItems: { xs: 'flex-start', md: 'flex-end' } }}>
            <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, fontWeight: 800, letterSpacing: 0.6 }}>
              Tri
            </Typography>
            <SortControl sort={sort} onSort={handleSort} />
          </Stack>
        </Stack>

        {renderBody()}

        <Popover
          open={Boolean(details.anchorEl)}
          anchorEl={details.anchorEl}
          onClose={closeDetails}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          {details.row ? <RowDetailsContent row={details.row} threshold={lowSampleThreshold} /> : null}
        </Popover>
      </Stack>
    </Paper>
  );
}

export default ConfigurablePatternPerformanceTable;
